import type { AccountingSplit, RenditionLine, RenditionInvoice } from '@/domain/rendition/Rendition'
import type { RenditionPreviewItemInput } from '@/domain/rendition/RenditionRepository'
import { calculateInvoiceTotals } from '@/utils/invoiceTotals'
import { lineNet, paymentLinesOf } from '@/application/rendition/lineAdapter'
import { splitsDistributionInvalid } from '@/utils/splitValidation'

export function splitsSumTo100(splits: AccountingSplit[]): boolean {
  // Only splits that actually reach the backend count — one with a cost center OR an account (both
  // individually optional), matching the payload filter and accountingValidation.hasIncompleteSplits.
  const sent = splits.filter((s) => s.costCenterId != null || s.accountId != null)
  if (sent.length === 0) return false
  // Delegate the 100% check to the platform's tolerance-based validator (magnitude-independent, scales
  // with the split count) instead of a fixed Math.round, so rendiciones matches the rest of the app.
  return !splitsDistributionInvalid(sent)
}

// ---- invoice totals (single source of truth: reused by the line table, the wizard summary and the payload) ----
// NOTE: In Renditions, taxes, withholdings and discounts operate at the per-line level (per-line column
// and line table). Invoice-level fields are kept in the calculation functions for domain completeness.

/** Gross subtotal of an invoice before any discounts (line or header) = Σ (unitPrice × qty). */
export function invoiceSubtotal(inv: RenditionInvoice): number {
  return inv.lines.reduce((a, l) => a + ((l.unitPrice || 0) * (l.quantity || 0)), 0)
}

export const ACCOUNTING_PALETTE = [
  '#4CAF50', '#2196F3', '#FF9800', '#9C27B0',
  '#F44336', '#00BCD4', '#FFEB3B', '#795548',
  '#E91E63', '#3F51B5', '#8BC34A', '#FF5722',
]

/**
 * Returns a stable color for a Cost Center + Account combination.
 * Used to paint matching splits with the same color across the UI.
 */
export function paletteColor(comboIndex: number): string {
  if (comboIndex < 0) return '#E0E0E0'
  return ACCOUNTING_PALETTE[comboIndex % ACCOUNTING_PALETTE.length]
}

/**
 * Identifies a split by its target allocation (CC + Account).
 * Allows grouping splits that point to the same accounting buckets.
 */
export function comboKey(split: { costCenterId?: number; accountId?: number }): string {
  return `${split.costCenterId ?? 'none'}-${split.accountId ?? 'none'}`
}

/** Everything taken off the invoice: each line's discount plus the receipt's own global discount. */
export function invoiceDiscountAmount(inv: RenditionInvoice): number {
  const net = inv.lines.reduce((a, l) => a + lineNet(l, inv.discountType), 0)
  return invoiceSubtotal(inv) - net + (inv.discountTotal || 0)
}

/** Total tax/withholding of a kind across the invoice: line-level taxes + invoice-level taxes. */
export function invoiceTaxAmount(inv: RenditionInvoice, code: 'TAX' | 'WITHHOLDING'): number {
  const ofKind = (taxes: RenditionLine['taxes']): number =>
    taxes.filter((t) => t.taxTypeCode === code).reduce((a, t) => a + (t.taxAmount || 0), 0)
  const lineSum = inv.lines.reduce((a, l) => a + ofKind(l.taxes), 0)
  const invoiceSum = ofKind(inv.taxes ?? [])
  return lineSum + invoiceSum
}

/**
 * Final accounting amount of an invoice: net + taxes − withholdings + tip − global discount, via the shared
 * `calculateInvoiceTotals` — the SAME formula the payment invoice form uses.
 */
export function invoiceFinalTotal(inv: RenditionInvoice, country?: string | null): number {
  const headerWithholdings = (inv.taxes ?? [])
    .filter((t) => t.taxTypeCode === 'WITHHOLDING')
    .map((t, wi) => ({
      id: String(t.id ?? `h-${wi}`),
      tax_type: t.taxName,
      tax_type_code: 'WITHHOLDING',
      percentage: t.taxPercentage || 0,
      tax_amount: t.taxAmount || 0,
      taxable_amount: t.taxableAmount || 0,
    }))
  const base = calculateInvoiceTotals({
    country,
    lines: paymentLinesOf(inv),
    headerWithholdings,
    tipAmount: inv.tip || 0,
    discountTotal: inv.discountTotal || 0,
    discountType: inv.discountType,
  }).total
  // why: invoice-level (non-withholding) taxes have no slot in the shared formula (it models line taxes
  // + header withholdings only); add them on top so invoiceFinalTotal stays consistent with
  // invoiceTaxAmount(inv, 'TAX'). Nothing writes inv.taxes today, but the domain models them.
  const invoiceLevelTax = (inv.taxes ?? [])
    .filter((t) => t.taxTypeCode === 'TAX')
    .reduce((a, t) => a + (t.taxAmount || 0), 0)
  return base + invoiceLevelTax
}

/**
 * The total an invoice header shows: the printed total of a pool-backed receipt, the own total of someone else's
 * shared receipt (its lines keep their origin's meaning, and read as nets they count the VAT twice), else its lines.
 */
export function invoiceDisplayTotal(inv: RenditionInvoice, country?: string | null): number | undefined {
  if (inv.documentFileId != null) return inv.nativeTotal
  if (inv.shareOwnedByOther && inv.nativeTotal != null) return inv.nativeTotal
  // An associated invoice with no lines yet shows the header total the backend already knew, not 0.
  const fromLines = invoiceFinalTotal(inv, country)
  return fromLines !== 0 ? fromLines : (inv.nativeTotal ?? 0)
}

/**
 * Native amounts of every draft invoice, in the shape the backend preview wants. The frontend owns
 * the NATIVE line math (instant while typing); the backend owns the rate, the conversion and the
 * summary. No `exchangeRate` leaves this function: summing at `?? 1` is how the same five boletas
 * showed CLP 50.097 and CLP 7.974.
 */
export function previewItemsFrom(invoices: RenditionInvoice[], country?: string | null): RenditionPreviewItemInput[] {
  return invoices.map((inv) => {
    const fromLines = invoiceFinalTotal(inv, country)
    return {
      currency: inv.currency,
      emissionDate: inv.emissionDate || undefined,
      total: fromLines !== 0 ? fromLines : (inv.nativeTotal ?? 0),
      netTotal: invoiceSubtotal(inv),
      discountTotal: invoiceDiscountAmount(inv),
      taxesTotal: invoiceTaxAmount(inv, 'TAX'),
      withholdingTotal: invoiceTaxAmount(inv, 'WITHHOLDING'),
      tipAmount: inv.tip || 0,
      ...(inv.shareOwnedByOther ? othersReceiptAmounts(inv, country) : {}),
      ...(inv.documentFileId != null && !inv.shareOwnedByOther ? { documentFileId: inv.documentFileId } : {}),
    }
  })
}

/**
 * Someone else's shared receipt as the preview prices it: its own total and the discount that total implies. It
 * names no pool file: the file is the owner's, and the backend would look for it in the requester's Mis Archivos.
 */
function othersReceiptAmounts(inv: RenditionInvoice, country?: string | null): Pick<RenditionPreviewItemInput, 'total' | 'discountTotal'> {
  const total = inv.nativeTotal ?? invoiceFinalTotal(inv, country)
  const net = total - (inv.tip || 0) - invoiceTaxAmount(inv, 'TAX') + invoiceTaxAmount(inv, 'WITHHOLDING')
  return { total, discountTotal: invoiceSubtotal(inv) - net }
}
